import type { OnDestroy, OnInit } from '@angular/core';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import type { IShipping } from '@shared/jp/data/shipping';
import type { IAddressShippingData, IOrderData } from '@shared/jp/interfaces';
import type { TCouponData } from '@shared/common/interfaces';

import { REGION_NAME_ENGLISH_DEFAULT } from '@shared/jp/shipping/constants';
import type {
  TBookShippingMethod,
  TShippingMethod,
} from '@shared/jp/shipping/interfaces';
import { getError } from '@shared/common/utils';
import { FirebaseService } from 'src/app/services/firebase.service';
import { LoaderService, ELoaderType } from '@studiobuki/web-core/lib/loader';
import { ProductionService } from 'src/app/services/production.service';
import { StripeService } from '@studiobuki/web-core/lib/stripe';
import { UserService } from '@studiobuki/web-core/lib/user';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  filter,
  combineLatest,
  map,
  distinctUntilChanged,
  take,
  firstValueFrom,
  Subject,
  shareReplay,
  takeUntil,
} from 'rxjs';
import { RoutingService } from 'src/app/services/routing.service';
import { MediaObserver } from '@angular/flex-layout';
import Subscriber from '@shared/common/subscriber';
import { Logger } from '@shared/common/logger';
import {
  GATrack,
  GAGetEvents,
  sendConversionEventByOrder,
} from 'src/analytics';
import type { TProductAlias } from '@shared/jp/product';
import type { TBookData } from '@shared/jp/models';
import { filterNullable, getNgIfAs } from '@studiobuki/web-core';
import { ROUTE_PATH_PARAM_ORDER_ID } from '@studiobuki/web-core/lib/routing';
import {
  getDefaultShipping,
  getOrderProductsCount,
} from '@shared/jp/shipping/utils';
import { EVendor } from '@shared/jp/enums';
import type { IFormData, TStepId } from './interfaces';
import { MainComponent } from './components/main/main.component';

const log = new Logger('OrderComponent');
const sub = new Subscriber();

@Component({
  templateUrl: './order-page.component.html',
  styleUrls: ['./order-page.component.scss'],
})
export class OrderPageComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  @ViewChild(MainComponent) mainComponent?: MainComponent;

  private _order$ = new BehaviorSubject<IOrderData | undefined>(undefined);

  public order$ = this._order$.pipe(filterNullable());

  public coupon$ = new BehaviorSubject<TCouponData | undefined>(undefined);

  private _formData$ = new BehaviorSubject<IFormData>({
    formEmail: undefined,
    formBillingAddress: undefined,
    formShippingAddress: undefined,
    formSameBillingAddress: true,
    formShippingMethod: undefined,
  });
  // public formData: FormData

  get formData() {
    return this._formData$.value;
  }

  set formData(formData: IFormData) {
    const prevFormData = this._formData$.value;

    this._formData$.next(formData);

    if (formData.formBillingAddress !== prevFormData.formBillingAddress) {
      this._onCustomerInfoUpdate(formData.formBillingAddress);
    }

    if (formData.formShippingMethod !== prevFormData.formShippingMethod) {
      this._onShippingMethodUpdate(formData.formShippingMethod);
    }

    if (formData.formShippingAddress !== prevFormData.formShippingAddress) {
      this._onShippingAdreessUpdate(formData.formShippingAddress);
    }
  }

  public disabledShippingMethods: TBookShippingMethod[] = [];

  public readonly productsCount$ = combineLatest([
    this.order$,
    this._firebaseService.products$,
  ]).pipe(
    map(([order, products]) => {
      const count = getOrderProductsCount(order, products);

      return count;
    }),
    distinctUntilChanged(isEqual as any),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  /** selected shipping */
  public shippings$ = this._productionService
    .getAllShippings$(
      combineLatest([this.order$, this._formData$]).pipe(
        map(([orderData, formData]) => {
          const regionName = formData.formShippingAddress?.state;

          return {
            booksCount: orderData.books.length,
            regionName: regionName || REGION_NAME_ENGLISH_DEFAULT,
          };
        }),
        distinctUntilChanged(isEqual),
      ),
    )
    .pipe(
      map(({ shippings, disabledShippingMethods }) => {
        this.disabledShippingMethods = disabledShippingMethods;

        return shippings;
      }),
      shareReplay(1),
      takeUntil(this._destroy$),
    );

  public readonly defaultShippingMethod$ = this.productsCount$.pipe(
    map((count) => {
      const defaultShipping = getDefaultShipping(count);

      return defaultShipping;
    }),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  public readonly shippingMethod$ = new BehaviorSubject<
    TShippingMethod | undefined
  >(undefined);

  public readonly shipping$ = combineLatest([
    this.defaultShippingMethod$,
    this.shippingMethod$.pipe(distinctUntilChanged()),
    this.shippings$,
  ]).pipe(
    map(([defaultShippingMethod, shippingMethod, shippings]) => {
      const method = shippingMethod || defaultShippingMethod;

      return shippings.find(({ id }) => id === method);
    }),
    filter((shipping): shipping is IShipping => !!shipping),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  private _step: TStepId = 1;

  public readonly steps = 3;

  set step(step: TStepId) {
    const { steps } = this;

    // min max check including thankyou step
    if (step > 0 && step <= steps + 1) {
      this._step = step;

      // not including thankyou step
      if (step <= steps) {
        GATrack(GAGetEvents.getCheckoutStep(step));
      } else {
        this.order$.pipe(take(1)).subscribe((orderData) => {
          // sendConversionEventByOrder(orderData);
          this._loaderService.hide();
        });
      }
    }

    document.documentElement.scrollTop = 0;
  }

  get step() {
    return this._step;
  }

  get isCompleted(): boolean {
    return this.step > this.steps;
  }

  // get isBackDisabled(): boolean {
  //   return false;
  // }

  public isBackDisabled = false;

  public isNextDisabled = false;

  public readonly getNgIfAs = getNgIfAs;

  constructor(
    private _productionService: ProductionService,
    public userService: UserService<TProductAlias, TBookData>,
    private _route: ActivatedRoute,
    private _router: Router,
    public media: MediaObserver,
    public stripeService: StripeService,
    private _loaderService: LoaderService,
    private _routingService: RoutingService,
    private _firebaseService: FirebaseService,
  ) {}

  async ngOnInit(): Promise<void> {
    const orderId = this._route.snapshot.paramMap.get(
      ROUTE_PATH_PARAM_ORDER_ID,
    );

    if (!orderId) {
      alert(`Order id is required!`);
      await this._routingService.goTo404();
      return;
    }

    this._loaderService.show();

    let order = await this._firebaseService.getUserOrder(orderId);

    if (!order) {
      log.error('!order');
      alert('Order not found!');
      await this._routingService.goTo404();
      return;
    }

    this._order$.next(order);

    const { error, barOrderId, sessionId } = order;

    if (error) {
      await this._handleError(error);
      return;
    }

    // check if order is already paid
    if (barOrderId) {
      this._handleComplete();
      return;
    }

    // * wait for payment
    if (sessionId) {
      // log.info('got sessionId', sessionId, new Date());
      this._loaderService.show({ type: ELoaderType.payment, data: {} });

      order = await this._firebaseService.waitForUserOrder(
        orderId,
        (_order) => {
          const hasBarOrderId =
            !!_order && 'barOrderId' in _order && !!_order.barOrderId;
          const hasError = !!_order && 'error' in _order && !!_order.error;

          return hasBarOrderId || hasError;
        },
      );

      this._order$.next(order);

      if ('error' in order && order.error) {
        await this._handleError(order.error);
        return;
      }

      if ('barOrderId' in order && order.barOrderId) {
        /** refresh the books state (should be removed) */
        await Promise.all(
          order.books.map(({ bookId }) =>
            this._firebaseService.loadUserBook(bookId),
          ),
        );

        /** refresh the products state (should be removed) */
        if (order.products) {
          await Promise.all(
            order.products.map(({ product: { docId } }) =>
              this._firebaseService.loadUserProduct(docId),
            ),
          );
        }

        this._handleComplete();
        return;
      }

      return;
    }

    /** load coupon that's attached to the order */
    let coupon;
    try {
      coupon = await this._firebaseService.loadStripeCoupon(orderId);
      this.coupon$.next(coupon);
    } catch (e) {
      log.info('no coupons');
    }

    this.step = 1;
    this._loaderService.hide();

    // FBTrack(FBEvents.initiateCheckout);
  }

  ngOnDestroy(): void {
    sub.unsubscribe();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onChange(data: IFormData) {
    this.formData = data;
    this.isNextDisabled = false;
  }

  private _onCustomerInfoUpdate(
    formBillingAddress?: IFormData['formBillingAddress'],
  ): void {
    log.info('_onCustomerInfoUpdate', formBillingAddress);
  }

  private _onShippingMethodUpdate(
    formShippingMethod?: IFormData['formShippingMethod'],
  ): void {
    log.info('_onShippingMethodUpdate', formShippingMethod);

    if (formShippingMethod) {
      this.shippingMethod$.next(formShippingMethod);
    }
  }

  private _onShippingAdreessUpdate(
    formShippingAddress?: IFormData['formShippingAddress'],
  ): void {
    log.info('_onShippingAdreessUpdate', formShippingAddress);
  }

  // private _getCurrentRegion(): IRegion {
  //   const { formBillingAddress, formShippingAddress } = this.formData;
  //   const i1 = parseInt(formShippingAddress?.addressFirst || '', 10);
  //   const i2 = parseInt(formBillingAddress?.addressFirst || '', 10);

  //   return Regions[i1 || i2 || 0];
  // }

  public async backClick() {
    log.info('backClick', this.step);

    if (this.step === 1) {
      await this._router.navigate(['/cart']);
      return;
    }

    this.step--;
  }

  public async nextClick() {
    log.info('nextClick: step', this.step);

    const isValid = await this._validateStep();

    if (!isValid) {
      this.isNextDisabled = true;
      return;
    }

    if (this.step === this.steps) {
      // await this._updateOrderData();

      // let sessionId = '';

      // const orderData = await firstValueFrom(
      //   this.order$.pipe(filter((_) => !!_.sessionId)),
      // );

      // sessionId = orderData.sessionId || '';
      this._loaderService.show(/* { type: ELoaderType.payment, data: {} } */);

      try {
        const sessionId = await this._createStripeCheckout();

        if (!sessionId) {
          this._loaderService.hide();
          throw new Error('!sessionId');
        }

        await this.pay(sessionId);
      } catch (e) {
        alert('Something went wrong please try again!');
        log.error(e);
        this._loaderService.hide();
        await this._routingService.goToCart();
      }
    } else {
      this.step++;
    }
  }

  private async pay(sessionId: string) {
    const stripe = await this.stripeService.stripe;
    log.info('pay', new Date());

    const {
      formEmail,
      formShippingAddress,
      formBillingAddress,
      formSameBillingAddress,
    } = this.formData;

    const _formBillingAddress =
      !formSameBillingAddress && formBillingAddress
        ? formBillingAddress
        : formShippingAddress;

    if (!formEmail || !_formBillingAddress) {
      alert('!formEmail || !formShippingAddress');
      return;
    }

    const orderData = await firstValueFrom(this.order$);

    // try {
    // resetOnBeforeUnload();
    await sendConversionEventByOrder(orderData);

    const res = await stripe.redirectToCheckout({ sessionId });

    if ('error' in res) {
      const { error } = res;

      // alert(error.message || error);
      throw error;
    }
    // } catch (error) {
    //   log.error(error);

    //   alert('お支払いにエラーが発生しました。');

    //   this.loaderService.hide();
    // }
  }

  private async _createStripeCheckout() {
    const {
      formEmail,
      formBillingAddress,
      formSameBillingAddress,
      formShippingAddress,
    } = this.formData;

    if (!formEmail || !formShippingAddress)
      throw getError('missing required parameters', {
        formEmail,
        formShippingAddress,
      });

    const shipping = await firstValueFrom(this.shipping$);

    const addressShippingData: IAddressShippingData = {
      ...formShippingAddress,
      email: formEmail.email,
    };
    // check if the tick 'Ship to the address above' is active
    const addressBillingData: IAddressShippingData =
      !formSameBillingAddress && formBillingAddress
        ? {
            ...formBillingAddress,
            email: formEmail.email,
          }
        : addressShippingData;
    const shippingMethod = shipping.id as TShippingMethod;

    let orderData = await firstValueFrom(this.order$);

    const res = await this._firebaseService.httpsCreateStripeCheckout({
      uid: orderData.uid,
      orderId: orderData.orderId,
      addressBillingData,
      addressShippingData,
      shippingMethod,
      vendor: EVendor.tokyoMetro,
    });

    orderData = await this._firebaseService.loadUserOrder(orderData.orderId);

    this._order$.next(orderData);

    log.info(res);

    return res.data;
  }

  /**
   * @returns `true` if valid
   * @returns `false` and touches all the inputs to show up errors if not
   */
  private async _validateStep(): Promise<boolean> {
    const { mainComponent, step } = this;

    if (!mainComponent) return false;

    let status = false;

    switch (step) {
      case 1: {
        const {
          formEmailComponent,
          formBillingAddressComponent,
          formShippingAddressComponent,
        } = mainComponent;

        if (!formEmailComponent || !formShippingAddressComponent) {
          // set status
          status = false;

          break;
        }

        const isFormEmailValid = formEmailComponent.formGroup.valid;

        const isFormShippingAddressValid =
          formShippingAddressComponent.formGroup.valid;

        const isFormBillingAddressValid = formBillingAddressComponent
          ? formBillingAddressComponent.formGroup.valid
          : true;

        // set status
        status =
          isFormEmailValid &&
          isFormShippingAddressValid &&
          isFormBillingAddressValid;

        // touch if not valid to show errors
        if (!status) {
          formEmailComponent.touch();
          await formShippingAddressComponent.touch();
          await formBillingAddressComponent?.touch();
        }

        break;
      }
      case 2:
        status = true;

        break;
      case 3: {
        status = true;

        break;
      }
      // no default
    }

    return status;
  }

  private async _handleError(error: string) {
    /** showing the main error in console for developers */
    log.error(error);
    /** showing user readable error */
    alert(
      // TODO: change to japanese
      'An error occurred while processing your order. Please try again.',
    );
    await this._routingService.goToIndex();
    this._loaderService.hide();
  }

  private _handleComplete() {
    this._loaderService.hide();
    // prevent doubling
    if (this.step === 4) return;
    this.step = 4;
  }
}
